"use client";
import React, { useState } from 'react';
import { Box, Typography, Button, IconButton, Snackbar, keyframes } from '@mui/material';
import { Favorite, Share, ContentCopy, Check } from '@mui/icons-material';

// Floating hearts animation
const floatUp = keyframes`
    0% {
        opacity: 0;
        transform: translateY(20px) scale(0.5);
    }
    20% {
        opacity: 1;
    }
    100% {
        opacity: 0;
        transform: translateY(-60px) scale(1.2);
    }
`;

const pulse = keyframes`
    0%, 100% {
        transform: scale(1);
    }
    50% {
        transform: scale(1.05);
    }
`;

// Custom X (Twitter) icon
const XIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
    </svg>
);

// Custom Facebook icon
const FacebookIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
    </svg>
);

export default function DonationSuccess({ onClose }) {
    const [copied, setCopied] = useState(false);
    const [showSnackbar, setShowSnackbar] = useState(false);
    const [canNativeShare, setCanNativeShare] = useState(false);

    const shareUrl = typeof window !== 'undefined' 
        ? `${window.location.origin}/support` 
        : 'https://sunhivemicroschool.org/support';
    const shareText = "I just donated to Sun Hive Microschool! Join me in supporting this amazing school 🐝";

    // Check if native share is available AND we're on mobile (not desktop Safari)
    React.useEffect(() => {
        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || 
            (navigator.maxTouchPoints && navigator.maxTouchPoints > 2);
        if (typeof navigator !== 'undefined' && navigator.share && isMobile) {
            setCanNativeShare(true);
        }
    }, []);

    const handleNativeShare = async () => {
        try {
            await navigator.share({
                title: 'Support Sun Hive Microschool',
                text: shareText,
                url: shareUrl,
            });
        } catch (err) {
            // User cancelled or share failed - that's ok
            if (err.name !== 'AbortError') {
                console.error('Share failed:', err);
            }
        }
    };

    const handleShareTwitter = () => {
        const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
        window.open(twitterUrl, '_blank', 'width=550,height=420');
    };

    const handleShareFacebook = () => {
        const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`;
        window.open(facebookUrl, '_blank', 'width=550,height=420');
    };

    const handleCopyLink = async () => {
        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopied(true);
            setShowSnackbar(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    const [hearts] = useState([
        { left: '15%', delay: '0s' },
        { left: '35%', delay: '0.3s' },
        { left: '55%', delay: '0.6s' },
        { left: '75%', delay: '0.2s' },
        { left: '85%', delay: '0.5s' },
    ]);

    return (
        <Box 
            sx={{ 
                p: { xs: 4, sm: 5, md: 6 }, 
                backgroundColor: 'background.paper',
                textAlign: 'center',
                position: 'relative',
                overflow: 'hidden',
            }}
        >
            {/* Floating Hearts */}
            {hearts.map((heart, i) => (
                <Favorite 
                    key={i}
                    sx={{
                        position: 'absolute',
                        bottom: '30%',
                        left: heart.left,
                        color: 'primary.light',
                        fontSize: '1rem',
                        opacity: 0,
                        animation: `${floatUp} 2s ease-out infinite`,
                        animationDelay: heart.delay,
                    }}
                />
            ))}

            {/* Main Heart Icon */}
            <Box 
                sx={{ 
                    mb: 3,
                    animation: `${pulse} 2s ease-in-out infinite`,
                }}
            >
                <Favorite sx={{ fontSize: 72, color: 'primary.main' }} />
            </Box>

            {/* Message */}
            <Typography 
                variant="h4" 
                component="h2" 
                sx={{ 
                    fontWeight: 700, 
                    color: 'text.primary', 
                    mb: 2,
                    fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                }}
            >
                You&apos;re Amazing!
            </Typography>
            
            <Typography 
                sx={{ 
                    color: 'text.secondary', 
                    mb: 1,
                    fontSize: { xs: '1rem', sm: '1.1rem' },
                    fontWeight: 500,
                }}
            >
                Thank you for believing in our students.
            </Typography>
            
            <Typography 
                sx={{ 
                    color: 'text.muted', 
                    mb: 3,
                    fontSize: '0.9rem',
                    maxWidth: '300px',
                    mx: 'auto',
                }}
            >
                A receipt will be sent to your email.
            </Typography>

            {/* Share Buttons */}
            <Box sx={{ mb: 4 }}>
                {!canNativeShare && (
                    <Typography 
                        variant="body2" 
                        sx={{ color: 'text.muted', mb: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}
                    >
                        <Share sx={{ fontSize: 16 }} /> Spread the word
                    </Typography>
                )}
                
                {canNativeShare ? (
                    // Native share button for mobile
                    <Button
                        variant="outlined"
                        onClick={handleNativeShare}
                        startIcon={<Share />}
                        sx={{
                            borderColor: 'divider',
                            color: 'text.primary',
                            borderRadius: '2rem',
                            px: 3,
                            '&:hover': { borderColor: 'primary.main', backgroundColor: 'background.accent' },
                        }}
                    >
                        Share
                    </Button>
                ) : (
                    // Fallback buttons for desktop
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5 }}>
                        <IconButton
                            onClick={handleShareTwitter}
                            aria-label="Share on X (Twitter)"
                            sx={{
                                backgroundColor: 'background.section',
                                color: 'text.primary',
                                '&:hover': { backgroundColor: 'background.hover' },
                            }}
                        >
                            <XIcon />
                        </IconButton>
                        <IconButton
                            onClick={handleShareFacebook}
                            aria-label="Share on Facebook"
                            sx={{
                                backgroundColor: 'background.section',
                                color: '#1877F2',
                                '&:hover': { backgroundColor: 'background.hover' },
                            }}
                        >
                            <FacebookIcon />
                        </IconButton>
                        <IconButton
                            onClick={handleCopyLink}
                            aria-label="Copy link"
                            sx={{
                                backgroundColor: 'background.section',
                                color: copied ? 'success.main' : 'text.primary',
                                '&:hover': { backgroundColor: 'background.hover' },
                            }}
                        >
                            {copied ? <Check /> : <ContentCopy />}
                        </IconButton>
                </Box>
                )}
            </Box>

            {/* Close Button */}
            <Button
                variant="contained"
                onClick={onClose}
                sx={{
                    py: 1.5,
                    px: 5,
                    backgroundColor: 'primary.main',
                    color: 'primary.contrastText',
                    fontWeight: 600,
                    borderRadius: '2rem',
                    fontSize: '1rem',
                    '&:hover': {
                        backgroundColor: 'primary.dark',
                    },
                }}
            >
                Continue Exploring
            </Button>

            {/* Copy success snackbar */}
            <Snackbar
                open={showSnackbar}
                autoHideDuration={2000}
                onClose={() => setShowSnackbar(false)}
                message="Link copied to clipboard!"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            />
        </Box>
    );
}
